import axios from 'axios';

// Always call the API on the SAME origin as the current page.
// This makes login/auth work seamlessly across the preview URL, the production URL
// (refer-build.emergent.host), AND any custom domain (e.g. harmonizer.online) the user
// adds in Emergent's Deploy panel — no CORS preflight, no cookie SameSite issues.
// Falls back to REACT_APP_BACKEND_URL only when not running in a browser (SSR/Node tests).
const getApiBase = () => {
  if (typeof window !== 'undefined' && window.location && window.location.origin) {
    return `${window.location.origin}/api`;
  }
  return `${process.env.REACT_APP_BACKEND_URL || ''}/api`;
};
const API = getApiBase();
const api = axios.create({ baseURL: API, withCredentials: true });

// --- Auto-refresh expired sessions ---
// When the 24h access_token expires, the backend returns 401. Instead of dumping the user
// with "Not authenticated", transparently call /auth/refresh (using the 7-day refresh_token
// cookie) once and retry the original request. Single-flight: concurrent 401s share one refresh.
let isRefreshing = false;
let refreshQueue = [];
const flushQueue = (error) => {
  refreshQueue.forEach(({ resolve, reject }) => (error ? reject(error) : resolve()));
  refreshQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    const status = error.response?.status;
    const url = original?.url || '';
    const isAuthCall = url.includes('/auth/login') || url.includes('/auth/refresh') || url.includes('/auth/register');

    if (status !== 401 || original?._retry || isAuthCall) {
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => refreshQueue.push({ resolve, reject })).then(() => api(original));
    }

    original._retry = true;
    isRefreshing = true;
    try {
      await api.post('/auth/refresh');
      flushQueue(null);
      return api(original);
    } catch (refreshErr) {
      flushQueue(refreshErr);
      if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
        window.location.assign('/login');
      }
      return Promise.reject(refreshErr);
    } finally {
      isRefreshing = false;
    }
  }
);

function formatError(detail) {
  if (!detail) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export { api, formatError, API };
